import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { tv } from "tailwind-variants";
import { P } from "@/components/Typography";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { usePostCompanyProfile } from "@/requests/hooks/collect.ts";
import { Select } from "@/components/Select";
import { getCompanyIndustries } from "@/utils/getCompanyIndustries.tsx";
import { Form } from "@/components/Form/index.ts";

const styles = tv({
  slots: {
    descriptionContainer: "mb-20",
    inputContainer: "max-w-400",
    buttonContainer: "my-45 max-w-400",
  },
});

declare interface IProps {
  companyProfile?: IGetCompanyProfile;
  isCompanyProfilePending: boolean;
}

const CompanyProfileForm = ({
  companyProfile,
  isCompanyProfilePending,
}: IProps) => {
  const { descriptionContainer, inputContainer, buttonContainer } = styles();

  const formSchema = object({
    companyName: string().required(
      t("settings.companyProfile.form.companyName.empty"),
    ),
    companyWebsiteUrl: string().required(
      t("settings.companyProfile.form.companyWebsiteUrl.empty"),
    ),
    companyIndustry: object({
      label: string().required(
        t("settings.companyProfile.form.companyIndustry.empty"),
      ),
      value: string().required(
        t("settings.companyProfile.form.companyIndustry.empty"),
      ),
    }),
  });

  type ICompanyProfileForm = InferType<typeof formSchema>;

  const {
    mutate: postCompanyProfile,
    isPending: isPostCompanyProfilePending,
    isSuccess: isPostCompanyProfileSuccess,
  } = usePostCompanyProfile();

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm<ICompanyProfileForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      companyName: companyProfile?.companyName,
      companyWebsiteUrl: companyProfile?.companyWebsiteUrl,
      companyIndustry: {
        value: companyProfile?.companyIndustry,
        label: companyProfile?.companyIndustry,
      },
    },
  });

  const handleOnSubmitPostCompanyProfile = (
    companyProfileForm: ICompanyProfileForm,
  ) => {
    postCompanyProfile({
      firstName: companyProfile?.firstName,
      lastName: companyProfile?.lastName,
      companyName: companyProfileForm.companyName,
      companyWebsiteUrl: companyProfileForm.companyWebsiteUrl,
      companyIndustry: companyProfileForm.companyIndustry.value,
    });
  };

  useEffect(() => {
    if (isPostCompanyProfileSuccess) {
      toast.success(t("settings.companyProfile.success"));
    }
  }, [isPostCompanyProfileSuccess]);

  const renderDescriptionSection = () => (
    <div className={descriptionContainer()}>
      <P>{t("settings.companyProfile.description")}</P>
    </div>
  );

  const renderCompanyProfileInputs = () => (
    <>
      <div className={inputContainer()}>
        <Input
          label={t("settings.companyProfile.form.companyName.label")}
          placeholder={t(
            "settings.companyProfile.form.companyName.placeholder",
          )}
          type="text"
          role="textbox"
          {...register("companyName")}
          helperText={errors?.companyName?.message}
          variant={!!errors?.companyName?.message ? "error" : undefined}
          testId="company-profile-company-name-input"
          id="company-profile-company-name-input"
        />
      </div>
      <div className={inputContainer()}>
        <Input
          label={t("settings.companyProfile.form.companyWebsiteUrl.label")}
          placeholder={t(
            "settings.companyProfile.form.companyWebsiteUrl.placeholder",
          )}
          type="text"
          role="textbox"
          {...register("companyWebsiteUrl")}
          helperText={errors?.companyWebsiteUrl?.message}
          variant={!!errors?.companyWebsiteUrl?.message ? "error" : undefined}
          testId="company-profile-website-url-input"
          id="company-profile-website-url-input"
        />
      </div>
      <div className={inputContainer()}>
        <Controller
          control={control}
          name="companyIndustry"
          render={({ field }) => (
            <Select
              aria-label={t(
                "settings.companyProfile.form.companyIndustry.label",
              )}
              options={getCompanyIndustries()}
              label={t("settings.companyProfile.form.companyIndustry.label")}
              placeholder={t(
                "settings.companyProfile.form.companyIndustry.placeholder",
              )}
              {...register("companyIndustry")}
              closeMenuOnSelect
              variant={!!errors?.companyIndustry?.message ? "error" : undefined}
              helperText={errors?.companyIndustry?.message}
              testId="company-profile-industry-input"
              id="company-profile-industry-input"
              aria-invalid={!!errors?.companyIndustry?.message}
              {...field}
            />
          )}
        />
      </div>
    </>
  );

  const renderButton = () => (
    <div className={buttonContainer()}>
      <Button
        as="button"
        variant="primary"
        type="submit"
        width="lg"
        extraClassNames="min-w-full"
        loading={isCompanyProfilePending || isPostCompanyProfilePending}
        disabled={isCompanyProfilePending || isPostCompanyProfilePending}
        testId="company-profile-cta"
        id="company-profile-cta">
        {t("settings.companyProfile.form.cta")}
      </Button>
    </div>
  );

  return (
    <Form onSubmit={handleSubmit(handleOnSubmitPostCompanyProfile)}>
      {renderDescriptionSection()}
      {renderCompanyProfileInputs()}
      {renderButton()}
    </Form>
  );
};

export default CompanyProfileForm;
